import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, notification } from 'antd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import {
  PushpinOutlined,
  CheckCircleOutlined,
  LockOutlined,
  FieldTimeOutlined,
  SendOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { CiViewTable } from 'react-icons/ci';
import { t } from '../../i18n/i18n';
import {
  updatePinnedRecruitSessions,
  getStageScreening,
  updateProctoringEnabledRecruitSessionStage,
} from '../../API/services/screeningService';
import { reOpenEvaluation } from '../../API/services/companyService';
import { shareRecruitSessionToHiringRoom } from '../../API/services/alkymersService';
import { disableForWorkspaceRole } from '../../utils/workspaceRoleValidation';
import { PartnerWorkspaceRole, CustomFeatureEnum, PlanFeaturesIds } from '../../constants/enums';
import {
  determineDisabledFeature,
  determineDisabledMinimumTier,
} from '../../utils/determineDisabled';
import useModalDetails from '../../hooks/useModalDetails';
import useSearchDashboard from '../../hooks/useSearchDashboard';
import useClickOutside from '../../hooks/useClickOutside';
import { getLocalStorageWorkspace } from '../../utils/workspaceUtils';
import Modal from '../Modal/Modal';
import DuplicateEvaluation from '../DuplicateEvaluation/DuplicateEvaluation';
import { ModalProctoringEnabled } from '../CardScreeningSearch/ModalProctoringEnabled';
import ModalMoveScreening from '../CardScreeningSearch/ModalMoveScreening';
import VerticalDotsDropdown from '../VerticalDotsDropdown/VerticalDotsDropdown';
import CopyCheck from '../../assets/search/Check';
import CopyLink from '../../assets/search/Link';
import EditPencilIcon from '../../assets/common/EditPencilIcon';
import DeleteIcon from '../../assets/profile/Delete';
import People from '../../assets/screeningSearchs/People';
import StatsIcon from '../../assets/screeningSearchs/StatsIcon';
import CalendarClose from '../../assets/screeningSearchs/CalendarClose';
import ArchiveBoxIcon from '../../assets/screeningSearchs/ArchiveBoxIcon';
import CameraProctoringEnabled from '../../assets/screeningSearchs/CameraProctoringEnabled';
import CameraProctoringDisabled from '../../assets/screeningSearchs/CameraProctoringDisabled';
import MoveScreeningIcon from '../../assets/screeningSearchs/MoveScreeningIcon';
import Reopen from '../../assets/screening/Reopen';
import CountryFlag from '../CountryFlag/CountryFlag';
import { useLanguageTranslations } from '../../hooks/useLanguageTranslations';
import { CREATE_EVALUATION } from '../../constants/routes';
import { TrackEventGTM } from '../../utils/filters/analyticsUtils';
import { EventsGTM } from '../../constants/events';
import './FeaturedEvaluationCard.scss';

const FeaturedEvaluationCard = ({ data, onPinChange, pinnedCount, reloadSearches }) => {
  const { user, currentRole, currentWorkspace } = useSelector((state) => state.auth);
  const history = useHistory();
  const { languagesList } = useLanguageTranslations();
  const cardRef = useRef(null);

  const { handleScreeningModalData, screeningModalContent, closeModal, loadingModal, showModal } =
    useModalDetails();
  const { showConfirmationModal, setShowConfirmationModal, confirmCloseSearchModalContent } =
    useSearchDashboard(data.recruitSessionId, true, reloadSearches);

  const [pinned, setPinned] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [publicIdCopySuccess, setPublicIdCopySuccess] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [disabledWorkspaceFunctions, setDisabledWorkspaceFunctions] = useState(true);
  const [reOpenLoading, setReOpenLoading] = useState(false);
  const [showProctoringEnabledModal, setShowProctoringEnabledModal] = useState(false);
  const [proctoringDataModal, setProctoringDataModal] = useState({
    proctoringEnabled: false,
    recruitSessionStageId: '',
  });
  const [showMoveScreeningModal, setShowMoveScreeningModal] = useState(false);
  const [moveScreeningDataModal, setMoveScreeningDataModal] = useState({
    partnerId: '',
    recruitSessionStageId: '',
  });
  const [hiringRoomSharedEvaluation, setHiringRoomSharedEvaluation] = useState(false);

  const today = new Date();
  const searchIsFinish = new Date(data.recruitSessionStageEndDate) < today;

  const hiringRoomIntegrated =
    user?.companyCustomFeatures?.customFeatures?.some(
      (cf) => cf?.featureEnum === CustomFeatureEnum.HIRING_ROOM
    ) && user?.companyCustomFeatures?.workspaceId === currentWorkspace;

  useEffect(() => {
    setDisabledWorkspaceFunctions(
      disableForWorkspaceRole(currentRole, [
        PartnerWorkspaceRole.OWNER,
        PartnerWorkspaceRole.ADMINISTRATOR,
      ])
    );
  }, [currentRole]);

  // useClickOutside(cardRef, () => setOpenDropdown(false)); // Removido - VerticalDotsDropdown maneja su propio click outside

  useEffect(() => {
    if (user && user.workspaces) {
      const partnerToWorkspace = user.workspaces.find(
        (workspace) => workspace.workspaceId === currentWorkspace
      );
      if (partnerToWorkspace) {
        const isPinned = partnerToWorkspace.pinnedRecruitSessions?.includes(data.recruitSessionId);
        setPinned(isPinned);
      }
    }
  }, [user, currentWorkspace, data.recruitSessionId]);

  const searchStatus = () => {
    if (!data.isActive) return { label: t('SEARCH_INACTIVE'), icon: <LockOutlined /> };
    if (searchIsFinish)
      return {
        label: t('SEARCH_EXPIRED'),
        icon: <FieldTimeOutlined style={{ color: '#686868' }} />,
      };
    if (new Date(data.recruitSessionStageStartDate) > today) return { label: t('SEARCH_SHEDULED') };
    return {
      label: t('SEARCH_ACTIVE'),
      icon: <CheckCircleOutlined style={{ color: '#4987be' }} />,
    };
  };

  const setBgColor = () => {
    if (!data.isActive) return 'closed-bg';
    if (searchIsFinish) return 'closed-bg';
    return 'open-bg';
  };

  const setTextColor = () => {
    if (!data.isActive) return '#272727';
    if (searchIsFinish) return '#272727';
    return '#4987be';
  };

  const handlePinned = async () => {
    try {
      const updatedWorkspace = await updatePinnedRecruitSessions({
        partnerId: user.id,
        pinnedRecruitSessions: [data.recruitSessionId],
      });
      setPinned(!pinned);
      notification.success({
        message: pinned ? t('SEARCH_UNPINNED') : t('SEARCH_PINNED'),
        duration: 3,
      });
      if (onPinChange) {
        if (pinned) {
          onPinChange('unpin', data.recruitSessionId, updatedWorkspace);
        } else {
          onPinChange('pin', data.recruitSessionId, updatedWorkspace);
        }
      }
    } catch {
      notification.error({
        message: t('ERROR_PINNING_SEARCH'),
        duration: 3,
      });
    }
  };

  const goToSearch = () => {
    history.push(`/search/${data.recruitSessionId}?workspaceId=${currentWorkspace}`);
  };

  const editStage = () => {
    setOpenDropdown(false);
    history.push(
      `/search/${data.recruitSessionId}?editDates=1?editName=1&workspaceId=${currentWorkspace}`
    );
  };

  const goToSearchStats = () => {
    setOpenDropdown(false);
    history.push(
      `/search/${data.recruitSessionId}?stats=${data.recruitSessionStageId}&workspaceId=${currentWorkspace}`
    );
  };

  const openModalForShowTests = async () => {
    const stage = await getStageScreening({
      companyId: getLocalStorageWorkspace()?.companyId,
      levelId: data.screeningId,
    });
    handleScreeningModalData(stage.id);
  };

  const closeEvaluation = () => {
    setOpenDropdown(false);
    setShowConfirmationModal(true);
  };

  const handleReOpenEvaluation = async (recruitSessionId) => {
    setOpenDropdown(false);
    setReOpenLoading(true);
    await reOpenEvaluation(recruitSessionId);
    if (reloadSearches) reloadSearches();
    setReOpenLoading(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(data.invitationLink);
    setCopySuccess(true);
    setTimeout(() => {
      setCopySuccess(false);
    }, 1000);
  };

  const copyStagePublicId = async () => {
    await navigator.clipboard.writeText(data.recruitSessionStagePublicId);
    setPublicIdCopySuccess(true);
    setTimeout(() => {
      setPublicIdCopySuccess(false);
    }, 1000);
  };

  const setProctoringShowModal = ({ proctoringEnabled, recruitSessionStageId = '' }) => {
    setOpenDropdown(false);
    setShowProctoringEnabledModal(true);
    setProctoringDataModal({
      proctoringEnabled,
      recruitSessionStageId,
    });
  };

  const onClickProctoringToggle = () => {
    updateProctoringEnabledRecruitSessionStage(proctoringDataModal.recruitSessionStageId, {
      proctoringEnabled: !proctoringDataModal.proctoringEnabled,
    })
      .then(() => {
        setShowProctoringEnabledModal(false);
        if (reloadSearches) reloadSearches();
      })
      .catch(() => {
        setShowProctoringEnabledModal(false);
      });
  };

  const setMoveScreeningModal = (pId, evalData) => {
    setOpenDropdown(false);
    setShowMoveScreeningModal(true);
    setMoveScreeningDataModal({
      partnerId: pId,
      id: evalData.recruitSessionStageId,
      isDraft: false,
    });
  };

  const sharedAssessmentToHiringRoom = async () => {
    try {
      setHiringRoomSharedEvaluation(true);
      const expired =
        new Date(
          user?.companyCustomFeatures?.customFeatures?.find(
            (i) => i.featureEnum === CustomFeatureEnum.HIRING_ROOM
          )?.authentication?.expiresAt
        ) < new Date();
      if (expired) {
        notification.open({
          message: t('MUST_REFRESH_TOKEN'),
          type: 'warning',
        });
        history.push('/integrations');
        return;
      }
      await shareRecruitSessionToHiringRoom({
        stageId: data.recruitSessionStageId,
      });
      notification.open({
        message: t('ASSESSMENT_SHARED_TO_HIRING_ROOM'),
        type: 'success',
      });
    } catch (e) {
      console.error('Error sharing assessment to Hiring Room:', e);
      notification.open({
        message: t('ERROR'),
        type: 'error',
        description: t('FAILED_TO_SHARE_ASSESSMENT'),
      });
    } finally {
      setHiringRoomSharedEvaluation(false);
    }
  };

  const dropdownOptions = [
    {
      icon: <EditPencilIcon className="icon" fill="#757575" width="14" height="14" />,
      text: t('EDIT'),
      disabled: false,
      action: () => editStage(),
      itemClass: `dropdown-option ${!data.isActive && 'd-none'}`,
      testid: 'edit-button',
    },
    {
      customContent: (
        <DuplicateEvaluation
          screeningId={data.screeningId}
          name={data.recruitSessionStageName}
          isDraft={false}
          disabled={disabledWorkspaceFunctions}
          isDropdown
          compact={true}
        />
      ),
      itemClass: 'dropdown-option',
      testid: 'duplicate-button',
    },
    {
      icon: <EditPencilIcon className="icon" fill="#757575" width="14" height="14" />,
      text: t('RE_OPEN'),
      disabled: false,
      action: () => handleReOpenEvaluation(data.recruitSessionId),
      itemClass: `dropdown-option ${
        data.isActive || new Date() > new Date(data.recruitSessionStageEndDate) ? 'd-none' : ''
      }`,
      testid: 'reopen-button',
    },
    {
      icon: <People className="icon" />,
      text: t('VIEW_CANDIDATES'),
      disabled: false,
      action: () => goToSearch(),
      itemClass: 'dropdown-option',
      testid: 'view-candidates-button',
    },
    {
      icon: <StatsIcon className="icon" width="14" height="16" />,
      text: t('VIEW_STATS'),
      disabled: false,
      action: () => goToSearchStats(),
      itemClass: 'dropdown-option',
      testid: 'view-stats-button',
    },
    {
      icon: data.proctoringEnabled ? (
        <CameraProctoringDisabled className="icon" />
      ) : (
        <CameraProctoringEnabled className="icon" />
      ),
      text: data.proctoringEnabled
        ? t('DISABLED_PROCTORING_DROPDOWN')
        : t('ENABLED_PROCTORING_DROPDOWN'),
      disabled: false,
      action: () =>
        setProctoringShowModal({
          proctoringEnabled: data.proctoringEnabled,
          recruitSessionStageId: data.recruitSessionStageId,
        }),
      itemClass: 'dropdown-option',
      testid: 'proctoring-enabled-button',
    },
    {
      icon: searchIsFinish ? (
        <ArchiveBoxIcon className="icon" />
      ) : (
        <CalendarClose className="icon" />
      ),
      text: searchIsFinish ? t('SCREENING_DASHBOARD_FINISH_SEARCH') : t('FINISH_EVALUATION'),
      disabled: false,
      action: () => closeEvaluation(),
      itemClass: `${searchIsFinish ? 'dropdown-option' : 'dropdown-delete-option'} ${
        !data.isActive && 'd-none'
      }`,
      testid: 'close-search-button',
    },
    {
      icon: <MoveScreeningIcon />,
      text: t('MOVE_WORKSPACE_MENU'),
      disabled: false,
      action: () => setMoveScreeningModal(user.id, data),
      itemClass: `${disabledWorkspaceFunctions ? 'd-none' : 'dropdown-option'}`,
      testid: 'move-search-icon',
    },
    {
      icon: publicIdCopySuccess ? <CopyCheck /> : <CopyLink height="16" />,
      text: publicIdCopySuccess ? t('COPIED_INVITATION_LINK') : t('COPY_PUBLIC_ID_LABEL'),
      disabled: determineDisabledMinimumTier(2) || !data.recruitSessionStagePublicId,
      action: () => copyStagePublicId(),
      itemClass: 'dropdown-option',
      testid: 'public-id-copy-icon',
    },
    {
      icon: hiringRoomSharedEvaluation ? <LoadingOutlined /> : <SendOutlined />,
      text: hiringRoomSharedEvaluation ? t('SHARING_HIRING_ROOM') : t('SHARED_HIRING_ROOM'),
      disabled: determineDisabledMinimumTier(2) || !hiringRoomIntegrated,
      action: () => sharedAssessmentToHiringRoom(),
      itemClass: `dropdown-option ${!hiringRoomIntegrated && 'd-none'}`,
      testid: 'share_test_hr_icon',
    },
  ];

  return (
    <div ref={cardRef} className="featured-evaluation-card">
      <Modal
        show={showModal}
        relativeCloseButton
        className="rounded px-4 py-2 justify-content-center w-80vw"
        showCloseButton={!loadingModal}
        closeBtnClassname="text-blue-principal font-weight-bold"
        setShow={(e) => closeModal(e)}
      >
        {screeningModalContent()}
      </Modal>
      <Modal
        show={showConfirmationModal}
        className="close-search-modal-container"
        closeBtnClassname="text-blue-principal font-weight-bold"
        setShow={setShowConfirmationModal}
        showCloseButton={false}
      >
        {confirmCloseSearchModalContent()}
      </Modal>
      <ModalProctoringEnabled
        setShowModal={setShowProctoringEnabledModal}
        showModal={showProctoringEnabledModal}
        data={proctoringDataModal}
        submitAction={onClickProctoringToggle}
        disabledButtonProps
      />
      <ModalMoveScreening
        setShowModal={setShowMoveScreeningModal}
        showModal={showMoveScreeningModal}
        data={moveScreeningDataModal}
        reloadSearches={reloadSearches}
      />

      <div className={`card-header ${setBgColor()}`}>
        <div className="card-title-row">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip className="">{data.recruitSessionStageName}</Tooltip>}
          >
            <button type="button" onClick={goToSearch} className="evaluation-title">
              {data.recruitSessionStageName}
            </button>
          </OverlayTrigger>

          <div className="card-actions">
            <div className="status-badge" style={{ color: setTextColor() }}>
              <span>{searchStatus().label?.toUpperCase()}</span>
              {searchStatus().icon}
            </div>

            <div className="header-buttons">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {pinned ? t('CARD_SCREENING_SEARCH_UNPIN') : t('CARD_SCREENING_SEARCH_PIN')}
                  </Tooltip>
                }
              >
                <Button
                  className="pin-button"
                  onClick={handlePinned}
                  disabled={!pinned && pinnedCount >= 6}
                >
                  <PushpinOutlined className={`icon-pin ${pinned ? 'pinned' : ''}`} />
                </Button>
              </OverlayTrigger>

              <VerticalDotsDropdown
                options={dropdownOptions}
                className="mr-2 width-selectable"
                setOpenDropdown={setOpenDropdown}
                openDropdown={openDropdown}
                disabled={disabledWorkspaceFunctions}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="info-row-compact">
          <span className="label-compact">{t('DASHBOARD_SEARCH_HEADERS_LIMIT_DATE')}</span>
          <span className="value-compact">
            {data.recruitSessionStageEmptyDates
              ? t('SCREENING_NO_LIMIT_DATE')
              : moment(data.recruitSessionStageEndDate).format('D MMM YYYY')}
          </span>
        </div>

        {(data.partnerFirstName || data.partnerLastName) && (
          <div className="created-by-compact">
            {`${t('RECRUIT_SESSION_DRAFT_CREATED_BY')} ${data.partnerFirstName} ${data.partnerLastName}`}
          </div>
        )}
      </div>

      <div className="card-footer">
        {searchIsFinish || !data.isActive ? (
          <div className="footer-actions">
            <button
              onClick={() => handleReOpenEvaluation(data.recruitSessionId)}
              className="footer-button"
              type="button"
              disabled={reOpenLoading}
            >
              <span>{t('RE_OPEN')}</span>
              <Reopen />
            </button>
            <DuplicateEvaluation
              screeningId={data.screeningId}
              name={data.recruitSessionStageName}
              isDraft={false}
              disabled={disabledWorkspaceFunctions}
            />
          </div>
        ) : (
          <div className="footer-actions">
            <button onClick={openModalForShowTests} className="footer-button" type="button">
              {t('VIEW_SCREENING_TEST')}
              <CiViewTable />
            </button>
            <button
              disabled={disabledWorkspaceFunctions}
              type="button"
              className={`footer-button ${
                determineDisabledFeature({ featureId: PlanFeaturesIds.SEARCH_LINKS })
                  ? 'd-none'
                  : ''
              }`}
              onClick={() => {
                TrackEventGTM(EventsGTM.EVALUATION_COPY_LINK, {
                  event_category: 'Featured evaluation card',
                  event_label: 'Copy Link',
                  event_action: 'click',
                });
                copyLink();
              }}
            >
              {copySuccess ? (
                <>
                  {t('COPIED_INVITATION_LINK')}
                  <CopyCheck />
                </>
              ) : (
                <>
                  {t('COPY_INVITATION_LINK')}
                  <CopyLink width="12" height="12" />
                </>
              )}
            </button>
            {data.language && (
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip placement="top">{t('SCREENING_LANGUAGE_SET')}</Tooltip>}
              >
                <span>
                  <CountryFlag
                    size="sm"
                    countryIso={languagesList.find((l) => l.value === data.language)?.iso}
                  />
                </span>
              </OverlayTrigger>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedEvaluationCard;
